import type { QualityLevel } from "../shared/types";

export interface UiCallbacks {
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onQualityChange: (quality: QualityLevel) => void;
  onSoundToggle: () => void;
  onFullscreen: () => void;
  onReset: () => void;
  onTouchMove: (x: number, y: number) => void;
  onTouchLook: (deltaX: number, deltaY: number) => void;
  onTouchAction: (action: "jump" | "interact" | "glide", pressed: boolean) => void;
}

function elementById<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`找不到畫面元件：${id}`);
  return element as T;
}

export class GameUi {
  private readonly callbacks: UiCallbacks;
  private readonly loadingScreen = elementById<HTMLElement>("loading-screen");
  private readonly loadingBar = elementById<HTMLElement>("loading-bar");
  private readonly loadingStatus = elementById<HTMLElement>("loading-status");
  private readonly startButton = elementById<HTMLButtonElement>("start-button");
  private readonly hud = elementById<HTMLElement>("hud");
  private readonly pauseScreen = elementById<HTMLElement>("pause-screen");
  private readonly resetConfirmationScreen = elementById<HTMLElement>("reset-confirmation-screen");
  private readonly resetButton = elementById<HTMLButtonElement>("reset-button");
  private readonly resetCancelButton = elementById<HTMLButtonElement>("reset-cancel-button");
  private readonly resetConfirmButton = elementById<HTMLButtonElement>("reset-confirm-button");
  private readonly unsupportedScreen = elementById<HTMLElement>("unsupported-screen");
  private readonly contextLostScreen = elementById<HTMLElement>("context-lost-screen");
  private readonly questText = elementById<HTMLElement>("quest-text");
  private readonly questProgress = elementById<HTMLElement>("quest-progress-bar");
  private readonly shardCount = elementById<HTMLElement>("shard-count");
  private readonly coreCount = elementById<HTMLElement>("core-count");
  private readonly interactionPrompt = elementById<HTMLElement>("interaction-prompt");
  private readonly interactionText = elementById<HTMLElement>("interaction-text");
  private readonly gliderIndicator = elementById<HTMLElement>("glider-indicator");
  private readonly locationToast = elementById<HTMLElement>("location-toast");
  private readonly locationName = elementById<HTMLElement>("location-name");
  private readonly compassNeedle = elementById<HTMLElement>("compass-needle");
  private readonly toastStack = elementById<HTMLElement>("toast-stack");
  private readonly soundButton = elementById<HTMLButtonElement>("sound-button");
  private readonly touchControls = elementById<HTMLElement>("touch-controls");
  private readonly joystickKnob = elementById<HTMLElement>("joystick-knob");
  private locationTimer: number | undefined;

  constructor(callbacks: UiCallbacks) {
    this.callbacks = callbacks;
    this.bindButtons();
    this.bindTouchControls();
  }

  setLoading(progress: number, status: string): void {
    this.loadingBar.style.width = `${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%`;
    this.loadingStatus.textContent = status;
  }

  markReady(status = "航線就緒，風正在等你。 "): void {
    this.setLoading(1, status);
    this.startButton.disabled = false;
  }

  showUnsupported(): void {
    this.loadingScreen.classList.remove("screen--visible");
    this.unsupportedScreen.classList.add("screen--visible");
  }

  showContextLost(): void {
    this.contextLostScreen.classList.add("screen--visible");
  }

  enterGame(): void {
    this.loadingScreen.classList.remove("screen--visible");
    this.hud.classList.add("hud--visible");
    this.hud.setAttribute("aria-hidden", "false");
    if (matchMedia("(pointer: coarse)").matches || window.innerWidth <= 820) {
      this.touchControls.classList.add("touch-controls--visible");
    }
  }

  setPaused(paused: boolean): void {
    if (!paused) this.hideResetConfirmation(false);
    this.pauseScreen.classList.toggle("screen--visible", paused);
    this.pauseScreen.setAttribute("aria-hidden", String(!paused));
    if (paused) elementById<HTMLButtonElement>("resume-button").focus();
  }

  setProgress(collectedShards: number, totalShards: number, activeCores: number, totalCores: number): void {
    this.shardCount.textContent = `${collectedShards} / ${totalShards}`;
    this.coreCount.textContent = `${activeCores} / ${totalCores}`;
    const coreRatio = totalCores === 0 ? 0 : activeCores / totalCores;
    const shardRatio = totalShards === 0 ? 0 : collectedShards / totalShards;
    this.questProgress.style.width = `${Math.round((coreRatio * 0.75 + shardRatio * 0.25) * 100)}%`;

    if (activeCores < totalCores) {
      this.questText.textContent = `喚醒風核塔（${activeCores} / ${totalCores}）`;
    } else if (collectedShards < totalShards) {
      this.questText.textContent = `前往星盤聖所，找回記憶（${collectedShards} / ${totalShards}）`;
    } else {
      this.questText.textContent = "所有風核與文明記憶都已復甦";
    }
  }

  showInteraction(label: string | null): void {
    this.interactionPrompt.classList.toggle("interaction-prompt--visible", label !== null);
    if (label) this.interactionText.textContent = `喚醒${label}`;
  }

  setGliding(gliding: boolean): void {
    this.gliderIndicator.classList.toggle("glider-indicator--visible", gliding);
  }

  setCompass(yaw: number): void {
    this.compassNeedle.style.transform = `rotate(${yaw}rad)`;
  }

  showLocation(label: string): void {
    this.locationName.textContent = label;
    this.locationToast.classList.add("location-toast--visible");
    if (this.locationTimer !== undefined) window.clearTimeout(this.locationTimer);
    this.locationTimer = window.setTimeout(() => {
      this.locationToast.classList.remove("location-toast--visible");
    }, 2600);
  }

  toast(title: string, detail?: string): void {
    const toast = document.createElement("div");
    toast.className = "game-toast";
    const heading = document.createElement("strong");
    heading.textContent = title;
    toast.appendChild(heading);
    if (detail) {
      toast.appendChild(document.createElement("br"));
      toast.appendChild(document.createTextNode(detail));
    }
    this.toastStack.appendChild(toast);
    window.setTimeout(() => toast.remove(), 4300);
  }

  setQuality(level: QualityLevel): void {
    document.querySelectorAll<HTMLButtonElement>("[data-quality]").forEach((button) => {
      const active = button.dataset.quality === level;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
    });
  }

  setSoundEnabled(enabled: boolean): void {
    this.soundButton.textContent = enabled ? "音效：開" : "音效：關";
    this.soundButton.setAttribute("aria-pressed", String(enabled));
  }

  private bindButtons(): void {
    this.startButton.addEventListener("click", this.callbacks.onStart);
    elementById<HTMLButtonElement>("pause-button").addEventListener("click", this.callbacks.onPause);
    elementById<HTMLButtonElement>("resume-button").addEventListener("click", this.callbacks.onResume);
    this.soundButton.addEventListener("click", this.callbacks.onSoundToggle);
    elementById<HTMLButtonElement>("fullscreen-button").addEventListener("click", this.callbacks.onFullscreen);
    this.resetButton.addEventListener("click", () => this.showResetConfirmation());
    this.resetCancelButton.addEventListener("click", () => this.hideResetConfirmation());
    this.resetConfirmButton.addEventListener("click", () => {
      this.resetConfirmButton.disabled = true;
      this.callbacks.onReset();
    });
    this.resetConfirmationScreen.addEventListener("click", (event) => {
      if (event.target === this.resetConfirmationScreen) this.hideResetConfirmation();
    });
    this.resetConfirmationScreen.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.hideResetConfirmation();
        return;
      }
      if (event.key !== "Tab") return;

      event.preventDefault();
      const buttons = [this.resetCancelButton, this.resetConfirmButton];
      const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const direction = event.shiftKey ? -1 : 1;
      const nextIndex = currentIndex < 0
        ? 0
        : (currentIndex + direction + buttons.length) % buttons.length;
      buttons[nextIndex].focus();
    });
    document.querySelectorAll<HTMLButtonElement>("[data-quality]").forEach((button) => {
      button.addEventListener("click", () => {
        const quality = button.dataset.quality;
        if (quality === "low" || quality === "medium" || quality === "high") {
          this.callbacks.onQualityChange(quality);
        }
      });
    });
    document.querySelectorAll<HTMLButtonElement>("[data-action='reload']").forEach((button) => {
      button.addEventListener("click", () => window.location.reload());
    });
  }

  private showResetConfirmation(): void {
    this.resetConfirmButton.disabled = false;
    this.pauseScreen.setAttribute("aria-hidden", "true");
    this.resetConfirmationScreen.setAttribute("aria-hidden", "false");
    this.resetConfirmationScreen.classList.add("screen--visible");
    this.resetCancelButton.focus();
  }

  private hideResetConfirmation(restoreFocus = true): void {
    const wasVisible = this.resetConfirmationScreen.classList.contains("screen--visible");
    this.resetConfirmationScreen.classList.remove("screen--visible");
    this.resetConfirmationScreen.setAttribute("aria-hidden", "true");
    if (this.pauseScreen.classList.contains("screen--visible")) {
      this.pauseScreen.setAttribute("aria-hidden", "false");
    }
    if (restoreFocus && wasVisible) this.resetButton.focus();
  }

  private bindTouchControls(): void {
    const moveZone = elementById<HTMLElement>("move-zone");
    const joystickBase = moveZone.querySelector<HTMLElement>(".joystick-base");
    const lookZone = elementById<HTMLElement>("look-zone");
    if (!joystickBase) throw new Error("找不到觸控搖桿");

    let movePointer: number | null = null;
    const updateMove = (event: PointerEvent): void => {
      const bounds = joystickBase.getBoundingClientRect();
      const centerX = bounds.left + bounds.width / 2;
      const centerY = bounds.top + bounds.height / 2;
      const radius = bounds.width * 0.38;
      let x = (event.clientX - centerX) / radius;
      let y = (event.clientY - centerY) / radius;
      const length = Math.hypot(x, y);
      if (length > 1) {
        x /= length;
        y /= length;
      }
      this.joystickKnob.style.transform = `translate(${x * radius}px, ${y * radius}px)`;
      this.callbacks.onTouchMove(x, y);
    };

    moveZone.addEventListener("pointerdown", (event) => {
      if (movePointer !== null) return;
      event.preventDefault();
      movePointer = event.pointerId;
      moveZone.setPointerCapture(event.pointerId);
      updateMove(event);
    });
    moveZone.addEventListener("pointermove", (event) => {
      if (event.pointerId === movePointer) updateMove(event);
    });
    const endMove = (event: PointerEvent): void => {
      if (event.pointerId !== movePointer) return;
      movePointer = null;
      this.joystickKnob.style.transform = "translate(0, 0)";
      this.callbacks.onTouchMove(0, 0);
    };
    moveZone.addEventListener("pointerup", endMove);
    moveZone.addEventListener("pointercancel", endMove);

    let lookPointer: number | null = null;
    let lastX = 0;
    let lastY = 0;
    lookZone.addEventListener("pointerdown", (event) => {
      if (lookPointer !== null) return;
      event.preventDefault();
      lookPointer = event.pointerId;
      lastX = event.clientX;
      lastY = event.clientY;
      lookZone.setPointerCapture(event.pointerId);
    });
    lookZone.addEventListener("pointermove", (event) => {
      if (event.pointerId !== lookPointer) return;
      this.callbacks.onTouchLook(event.clientX - lastX, event.clientY - lastY);
      lastX = event.clientX;
      lastY = event.clientY;
    });
    const endLook = (event: PointerEvent): void => {
      if (event.pointerId === lookPointer) lookPointer = null;
    };
    lookZone.addEventListener("pointerup", endLook);
    lookZone.addEventListener("pointercancel", endLook);

    document.querySelectorAll<HTMLButtonElement>("[data-touch-action]").forEach((button) => {
      const action = button.dataset.touchAction;
      if (action !== "jump" && action !== "interact" && action !== "glide") return;
      button.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        event.stopPropagation();
        button.setPointerCapture(event.pointerId);
        this.callbacks.onTouchAction(action, true);
      });
      const release = (event: PointerEvent): void => {
        event.preventDefault();
        this.callbacks.onTouchAction(action, false);
      };
      button.addEventListener("pointerup", release);
      button.addEventListener("pointercancel", release);
    });
  }
}
